import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, UserCheck, Users, Building2, CheckSquare, Square, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AssignAccounts = ({ onLogout }) => {
  const [accounts, setAccounts] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAssigned, setFilterAssigned] = useState('all'); // all / assigned / unassigned
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignTo, setAssignTo] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [accRes, userRes] = await Promise.all([
        axios.get(`${API}/accounts`, { headers }),
        axios.get(`${API}/auth/users`, { headers }),
      ]);
      setAccounts(accRes.data);
      // Only non-admin users are reps
      setReps(userRes.data.filter(u => !u.is_admin && u.is_active));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Filter accounts
  const filteredAccounts = accounts.filter(acc => {
    const matchesSearch = !searchQuery ||
      acc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (acc.city || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      filterAssigned === 'all' ||
      (filterAssigned === 'assigned' && acc.assigned_to) ||
      (filterAssigned === 'unassigned' && !acc.assigned_to);
    return matchesSearch && matchesFilter;
  });

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredAccounts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAccounts.map(a => a.id));
    }
  };

  const handleAssign = async () => {
    if (selectedIds.length === 0) { toast.error('Please select at least one account'); return; }
    if (!assignTo) { toast.error('Please select a representative'); return; }
    setAssigning(true);
    try {
      const token = localStorage.getItem('token');
      const rep = reps.find(r => r.id === assignTo);
      await axios.post(`${API}/accounts/assign`, {
        account_ids: selectedIds,
        assigned_to: rep.email
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${selectedIds.length} account${selectedIds.length > 1 ? 's' : ''} assigned to ${rep.name}!`);
      setSelectedIds([]);
      setAssignTo('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassign = async () => {
    if (selectedIds.length === 0) { toast.error('Please select at least one account'); return; }
    if (!window.confirm(`Unassign ${selectedIds.length} account(s)?`)) return;
    setAssigning(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/accounts/assign`, {
        account_ids: selectedIds,
        assigned_to: null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${selectedIds.length} account(s) unassigned`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to unassign');
    } finally {
      setAssigning(false);
    }
  };

  const getRepName = (email) => {
    const rep = reps.find(r => r.email === email);
    return rep ? rep.name : email?.split('@')[0] || '—';
  };

  const allSelected = filteredAccounts.length > 0 && selectedIds.length === filteredAccounts.length;

  return (
    <Layout onLogout={onLogout}>
      <div className="p-8" data-testid="assign-accounts-page">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Assign Accounts
          </h1>
          <p className="text-base text-slate-500 mt-2">
            Assign accounts to your sales representatives
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white/80 rounded-2xl border border-slate-200 shadow p-4 flex items-center gap-4">
            <div className="p-3 bg-teal-100 rounded-xl"><Building2 className="h-5 w-5 text-teal-700" /></div>
            <div>
              <p className="text-sm text-slate-500">Total Accounts</p>
              <p className="text-2xl font-bold text-slate-800">{accounts.length}</p>
            </div>
          </div>
          <div className="bg-white/80 rounded-2xl border border-slate-200 shadow p-4 flex items-center gap-4">
            <div className="p-3 bg-emerald-100 rounded-xl"><UserCheck className="h-5 w-5 text-emerald-600" /></div>
            <div>
              <p className="text-sm text-slate-500">Assigned</p>
              <p className="text-2xl font-bold text-slate-800">{accounts.filter(a => a.assigned_to).length}</p>
            </div>
          </div>
          <div className="bg-white/80 rounded-2xl border border-slate-200 shadow p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-xl"><Users className="h-5 w-5 text-amber-600" /></div>
            <div>
              <p className="text-sm text-slate-500">Unassigned</p>
              <p className="text-2xl font-bold text-slate-800">{accounts.filter(a => !a.assigned_to).length}</p>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        {selectedIds.length > 0 && (
          <div className="mb-4 p-4 bg-teal-50 border border-teal-200 rounded-2xl flex items-center gap-4 flex-wrap">
            <span className="text-teal-700 font-semibold text-sm">
              {selectedIds.length} account{selectedIds.length > 1 ? 's' : ''} selected
            </span>
            <Select value={assignTo} onValueChange={setAssignTo}>
              <SelectTrigger className="h-10 w-56 bg-white">
                <SelectValue placeholder="Select representative..." />
              </SelectTrigger>
              <SelectContent>
                {reps.map(rep => (
                  <SelectItem key={rep.id} value={rep.id}>
                    {rep.name} ({rep.email.split('@')[0]})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleAssign}
              disabled={assigning || !assignTo}
              className="h-10 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl font-semibold"
            >
              <UserCheck className="mr-2 h-4 w-4" />
              {assigning ? 'Assigning...' : 'Assign'}
            </Button>
            <Button
              onClick={handleUnassign}
              disabled={assigning}
              variant="outline"
              className="h-10 border-red-300 text-red-600 hover:bg-red-50 rounded-xl"
            >
              Unassign
            </Button>
            <button
              onClick={() => setSelectedIds([])}
              className="text-sm text-slate-400 hover:text-slate-600 ml-auto"
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Search + Filter */}
        <div className="flex gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search accounts by name or city..."
              className="pl-10 h-12 rounded-xl border-slate-200 focus:border-teal-500"
            />
          </div>
          <Select value={filterAssigned} onValueChange={setFilterAssigned}>
            <SelectTrigger className="h-12 w-48 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Accounts</SelectItem>
              <SelectItem value="assigned">Assigned Only</SelectItem>
              <SelectItem value="unassigned">Unassigned Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Accounts Table */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-xl">
          {/* Table Header */}
          <div className="p-4 border-b border-slate-100 flex items-center gap-4">
            <button
              onClick={toggleSelectAll}
              className="text-slate-400 hover:text-teal-700 transition-colors"
            >
              {allSelected
                ? <CheckSquare className="h-5 w-5 text-teal-700" />
                : <Square className="h-5 w-5" />
              }
            </button>
            <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex-1">
              Account Name
            </span>
            <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide w-32 hidden md:block">
              City
            </span>
            <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide w-40">
              Assigned To
            </span>
            <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide w-24">
              Status
            </span>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-slate-100">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-teal-500 border-t-transparent"></div>
                <p className="text-slate-500 mt-4">Loading accounts...</p>
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Building2 className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>No accounts found</p>
              </div>
            ) : (
              filteredAccounts.map(account => {
                const isSelected = selectedIds.includes(account.id);
                const hasIncomplete = account.contacts?.some(
                  c => !c.designation || c.designation === 'TBD' || !c.whatsapp_number || !c.can_influence_decisions
                ) || !account.address || account.address === 'TBD';

                return (
                  <div
                    key={account.id}
                    className={`flex items-center gap-4 p-4 hover:bg-slate-50/50 transition-colors ${isSelected ? 'bg-teal-50/50' : ''}`}
                  >
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleSelect(account.id)}
                      className="text-slate-400 hover:text-teal-700 transition-colors flex-shrink-0"
                    >
                      {isSelected
                        ? <CheckSquare className="h-5 w-5 text-teal-700" />
                        : <Square className="h-5 w-5" />
                      }
                    </button>

                    {/* Account Name */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-800 truncate">{account.name}</p>
                        <span className="text-xs font-mono bg-teal-100 text-teal-700 px-2 py-0.5 rounded">
                          {account.account_id}
                        </span>
                        {hasIncomplete && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-lg flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Incomplete
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {account.contacts?.length || 0} contact(s)
                        {account.contacts?.[0]?.name && account.contacts[0].name !== 'TBD'
                          ? ` · ${account.contacts[0].name}`
                          : ''}
                      </p>
                    </div>

                    {/* City */}
                    <span className="text-sm text-slate-500 w-32 hidden md:block truncate">
                      {account.city || '—'}
                    </span>

                    {/* Assigned To */}
                    <div className="w-40">
                      {account.assigned_to ? (
                        <span className="text-sm font-medium text-teal-700 bg-teal-50 px-2 py-1 rounded-lg">
                          {getRepName(account.assigned_to)}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">—</span>
                      )}
                    </div>

                    {/* Status */}
                    <div className="w-24">
                      {account.assigned_to ? (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-lg font-medium">
                          Assigned
                        </span>
                      ) : (
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded-lg font-medium">
                          Unassigned
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default AssignAccounts;
